import { useTranslation } from 'react-i18next';
import { Card, CardContent, Box, IconButton } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	API_TIME_FORMAT,
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_MINUTE_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MeditationListSummaryFilterSection from './components/MemberInterventionSummaryFilterSection';
import { CaseFilter } from '@/api/DTO/case-management/caseDto';
import { useGetMemberInterventionListQuery } from '@/api/case-management/caseApi';
import EditCaseNoteDialog from '@/pages/case-management/EditCaseNoteDialog';

export default function MemberInterventionSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [openCurrentId, setOpenCurrentId] = useState('');

	const defaultPropertyFilter: CaseFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		castNoteStatusList: [],
		additionalList: [],
		natureList: [],
		interventionWorkerList: [],
		submittedByList: [],
	};
	const defaultValues: DefaultValues<CaseFilter> = defaultPropertyFilter;
	const methods = useForm<CaseFilter>({
		defaultValues,
	});
	const transformedSearchParams: CaseFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'castNoteStatusList',
			'additionalList',
			'natureList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMemberInterventionListQuery(
			{
				...transformedSearchParams,
				id: admissionRequestId,
				dateRangeStart: methods.getValues('dateRangeStart'),
				dateRangeEnd: methods.getValues('dateRangeEnd'),
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				interventionDate: !item.interventionDate
					? '-'
					: dayjs(item.interventionDate)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
				interventionStartTime: !item.interventionStartTime
					? '-'
					: dayjs(item.interventionStartTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
				interventionEndTime: !item.interventionEndTime
					? '-'
					: dayjs(item.interventionEndTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as CaseFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'interventionDate',
			headerName: t('case.date'),
			flex: 15,
		},
		{
			field: 'interventionStartTime',
			headerName: t('case.startTime'),
			flex: 15,
		},
		{
			field: 'interventionEndTime',
			headerName: t('case.endTime'),
			flex: 15,
		},
		{
			field: 'natureDesc',
			headerName: t('case.nature'),
			flex: 15,
		},

		{
			field: 'additionalDesc',
			headerName: t('case.additional'),
			flex: 15,
		},
		{
			field: 'riskLevelDesc',
			headerName: t('case.riskLevel'),
			flex: 15,
		},

		{
			field: 'familyInvolvedAlliedHealthServicesEscortDesc',
			headerName: t('case.familyInvolvedAlliedHealthServicesEscort'),
			flex: 15,
		},
		{
			field: 'supportingStaffDesc',
			headerName: t('case.supportingStaff'),
			flex: 15,
		},
		{
			field: 'interventionWorkerName',
			headerName: t('case.inWorker'),
			flex: 15,
		},
		{
			field: 'submittedByName',
			headerName: t('case.submittedBy'),
			flex: 15,
		},
		{
			field: 'caseNoteStatusDesc',
			headerName: t('case.caseNoteStatus'),
			flex: 15,
		},
		// 因應需求，暫時隱藏case note的編輯功能，之後如果有需要再開發，但要注意case note的編輯功能會有比較複雜的權限控管問題
		// {
		// 	field: 'caseNote',
		// 	headerName: t('case.updateCaseNote'),

		// 	width: 68,
		// 	renderCell: (params) =>
		// 		params.row.caseNoteStatus != CaseNoteStatusEnum.Submitted && (
		// 			<IconButton
		// 				color='secondary'
		// 				onClick={() => {
		// 					setOpenCurrentId(params.row.id);
		// 					setOpen(true);
		// 				}}
		// 			>
		// 				<EditOutlinedIcon />
		// 			</IconButton>
		// 		),
		// },

		{
			field: 'edit',
			headerName: '',

			width: 68,
			renderCell: (params) => (
				<Link to={`${location.pathname}/intervention/detail/${params.id}`}>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<EditCaseNoteDialog
				open={open}
				setOpen={setOpen}
				openCurrentId={openCurrentId}
			/>
			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
